import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { CircleCheckBig, CircleMinus, OctagonX, type LucideIcon } from 'lucide-react';

import type { ConnectionHealth, IntegrationStatus, SyncLogView } from '../services/pmsConnectionApi';
import type { VendorDefinition } from '../vendor-settings/types';
import { landingCopy as copy } from '../landing/copy';

dayjs.extend(relativeTime);

/** The hero only exists for a connected account. */
export type ConnectedStatus = Exclude<IntegrationStatus, { kind: 'none' }>;

/** Which precedence row won; the message line keys on this, never on the label's copy. */
export type HeroPillKind = 'disabled' | 'inactive' | 'refresh-failed' | 'active';

export interface HeroPill {
  kind: HeroPillKind;
  variant: 'muted' | 'destructive' | 'success';
  label: string;
  icon: LucideIcon | null;
}

/** What a card paints for a pill: a page may show one that no `HeroPillKind` names (DV-3500's Checking). */
export type PillDisplay = Pick<HeroPill, 'variant' | 'label' | 'icon'>;

/**
 * Spec §4.4 pill precedence, first match wins. Health is the server-side
 * stored-credential probe (fails open to `unknown` until the backend endpoint
 * is deployed); the sync log is the fallback red signal.
 */
export const deriveHeroPill = (status: ConnectedStatus, health: ConnectionHealth, sync: SyncLogView): HeroPill => {
  if (status.kind === 'disabled') {
    return { kind: 'disabled', variant: 'muted', label: copy.hero.pill.disabled, icon: CircleMinus };
  }
  if (health.kind === 'unhealthy') {
    return { kind: 'inactive', variant: 'destructive', label: copy.hero.pill.inactive, icon: OctagonX };
  }
  if (health.kind === 'unknown' && sync.kind === 'failed') {
    return { kind: 'refresh-failed', variant: 'destructive', label: copy.hero.pill.refreshFailed, icon: OctagonX };
  }
  return { kind: 'active', variant: 'success', label: copy.hero.pill.active, icon: CircleCheckBig };
};

export type HeroMessage = string | null;

/**
 * Message line under the header, keyed on the pill that won. It explains the
 * CONNECTION state only; a failed refresh is reported by the sync line off
 * `sync.kind` alone (spec §4.4 correction, 2026-09-02), so the 'refresh-failed'
 * pill — a credential proxy while health is `unknown` — adds no message here.
 * `messages` lets a page say "paused" and "inactive" in its own voice
 * (DV-3500: the internal account page is not the customer, and can't act on
 * the connection). Each falls back to the customer copy.
 */
export const deriveHeroMessage = (
  status: ConnectedStatus,
  pill: HeroPill,
  vendor: VendorDefinition,
  messages: { disabled?: string; inactive?: string } = {},
): HeroMessage => {
  if (status.kind === 'disabled') return messages.disabled ?? copy.hero.message.disabled;
  if (pill.kind === 'inactive') return messages.inactive ?? vendor.inactiveMessage;
  return null;
};

export interface HeroFact {
  label: string;
  value: string;
}

/** Null for anything dayjs can't parse — a dropped fact beats rendering "Invalid Date". */
const onDate = (iso: string | null): string | null => {
  if (!iso) return null;
  const parsed = dayjs(iso);
  return parsed.isValid() ? parsed.format('MMM D, YYYY') : null;
};

/** How a fact with no value shows: left out (the customer card), or as a dash (DV-3500, the internal account page). */
export type MissingFacts = 'omit' | 'dash';
export const MISSING_VALUE = '—';

const fact = (label: string, value: string | null, missing: MissingFacts): HeroFact | null => {
  if (value) return { label, value };
  return missing === 'dash' ? { label, value: MISSING_VALUE } : null;
};

/**
 * Label/value pairs. In `omit` mode (default) a pair is left out when its
 * value is null (pre-DV-2550 connections have no attribution), and flag-off
 * (`unavailable`) shows no attribution at all — parity with today's UI
 * (spec §2 decision 10). In `dash` mode every fact is kept and a missing value
 * reads "—" (DV-3500: "a dash for any value not recorded"); Last property
 * refresh is a dash unless the latest run completed.
 */
export const deriveHeroFacts = (
  status: ConnectedStatus,
  sync: SyncLogView,
  now: Date = new Date(),
  missing: MissingFacts = 'omit',
): HeroFact[] => {
  const enabledBy = status.kind === 'unavailable' ? null : status.enabledBy;
  const enabledAt = status.kind === 'unavailable' ? null : status.enabledAt;
  const attribution =
    status.kind === 'unavailable' && missing === 'omit'
      ? []
      : [
          fact(copy.hero.facts.enabledBy, enabledBy, missing),
          fact(copy.hero.facts.enabledOn, onDate(enabledAt), missing),
          ...(status.kind === 'disabled'
            ? [
                fact(copy.hero.facts.disabledBy, status.disabledBy, missing),
                fact(copy.hero.facts.disabledOn, onDate(status.disabledAt), missing),
              ]
            : []),
        ];
  const lastRefreshValue = sync.kind === 'complete' && sync.endedAt ? dayjs(sync.endedAt).from(dayjs(now)) : null;
  return [
    fact(copy.hero.facts.connectedOn, onDate(status.connection.connectedAt), missing),
    ...attribution,
    fact(copy.hero.facts.lastRefresh, lastRefreshValue, missing),
  ].filter((entry): entry is HeroFact => entry !== null);
};

export interface HeroActions {
  refresh: boolean;
  settings: boolean;
  viewSettings: boolean;
  reenable: boolean;
}

/** Spec §4.4 actions table. Only Re-enable is role-gated (DV-2549 §2 decision 3). */
export const deriveHeroActions = (status: ConnectedStatus, sync: SyncLogView, canManage: boolean): HeroActions => {
  const disabled = status.kind === 'disabled';
  return {
    refresh: !disabled && sync.kind !== 'running',
    settings: !disabled,
    viewSettings: disabled,
    reenable: disabled && canManage,
  };
};

/** `sunbeltres.rentvine.com` from a stored URL or bare subdomain; null when there is nothing to show (Buildium). */
export const accountHost = (accountUrl: string | null): string | null => {
  if (!accountUrl) return null;
  try {
    return new URL(accountUrl).host || null;
  } catch {
    return accountUrl.replace(/\/+$/, '') || null;
  }
};
